// EntityPicker — the searchable picker that replaces the composer's flat
// `<select>` lists so a large model doesn't dump an overwhelming scroll. One
// component, two modes:
//   - mode="tree" (elements): a containment tree from dotted qualified paths,
//     expand/collapse, auto-opened down to the current value; typing flattens
//     to fuzzy-ranked matches.
//   - mode="list" (types, aspect keys): a flat, fuzzy-filtered list.
// Keyboard: ↑/↓ move, Enter selects (or expands a container), →/← expand /
// collapse, Esc dismisses (via the Popover). All layout/filter logic is pure in
// `entityTree.ts`; this file is chrome.
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { ChevronRight, ChevronDown } from "lucide-react";
import { buttonClasses, useContextMenu, type ContextMenuItem } from "@archlang/ui";
import { Popover, PopoverTrigger, PopoverPanel } from "../Popover.js";
import { buildTree, flattenVisible, filterItems, ancestorsOf } from "./entityTree.js";

export interface EntityPickerProps {
  readonly value: string | null;
  readonly onChange: (value: string | null) => void;
  readonly items: readonly string[];
  readonly mode?: "tree" | "list";
  /** Trigger text when nothing is picked. */
  readonly placeholder?: string;
  /** When set, a clear-to-null option at the top of the list. */
  readonly noneLabel?: string;
  /** Accessible name of the field — the trigger's aria-label. */
  readonly "aria-label": string;
  readonly title?: string;
  readonly disabled?: boolean;
  /** Extra classes merged onto the trigger (call sites slot it into a flex row). */
  readonly className?: string;
}

// Field-shaped trigger: the kit owns paint/size/focus/disabled; only the
// left-aligned field layout + border are ours.
const TRIGGER_BASE = buttonClasses(
  "secondary",
  "xs",
  "justify-start border border-slate-700 text-left font-normal",
);

export function EntityPicker(props: EntityPickerProps): ReactElement {
  const [open, setOpen] = useState(false);
  const { value, placeholder = "pick…", "aria-label": label, disabled, title, className } = props;

  return (
    <Popover open={open} onOpenChange={setOpen} placement="bottom-start">
      <PopoverTrigger>
        {({ ref, props: trig }) => (
          <button
            ref={ref}
            {...trig}
            type="button"
            aria-label={label}
            title={title}
            disabled={disabled}
            className={`${TRIGGER_BASE} ${className ?? ""}`}
          >
            <span className={`flex-1 truncate ${value ? "" : "text-slate-500"}`}>{value ?? placeholder}</span>
            <span aria-hidden="true" className="opacity-60">
              ▾
            </span>
          </button>
        )}
      </PopoverTrigger>
      <PopoverPanel className="z-overlay w-64 rounded-lg border border-slate-700 bg-slate-900/(--tint-solid) p-1 shadow-xl backdrop-blur-md outline-none">
        <PickerBody {...props} onDone={() => setOpen(false)} />
      </PopoverPanel>
    </Popover>
  );
}

interface Row {
  readonly path: string;
  readonly label: string;
  readonly depth: number;
  readonly selectable: boolean;
  readonly hasChildren: boolean;
}

function PickerBody({
  items,
  mode = "list",
  value,
  onChange,
  noneLabel,
  "aria-label": label,
  onDone,
}: EntityPickerProps & { onDone: () => void }): ReactElement {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const menu = useContextMenu();
  const tree = useMemo(() => (mode === "tree" ? buildTree(items) : []), [items, mode]);
  // Open the tree down to the current value so it's visible on first paint.
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(mode === "tree" && value ? ancestorsOf(value) : []),
  );

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filtering = query.trim().length > 0;
  const rows: Row[] = useMemo(() => {
    if (mode === "tree" && !filtering) {
      return flattenVisible(tree, expanded).map((n) => ({
        path: n.path,
        label: n.label,
        depth: n.depth,
        selectable: n.selectable,
        hasChildren: n.children.length > 0,
      }));
    }
    // List mode, or a filtered tree → flat ranked matches (full path shown).
    return filterItems(items, query).map((p) => ({
      path: p,
      label: p,
      depth: 0,
      selectable: true,
      hasChildren: false,
    }));
  }, [mode, filtering, tree, expanded, items, query]);

  useEffect(() => {
    if (active >= rows.length) setActive(Math.max(0, rows.length - 1));
  }, [rows.length, active]);

  const choose = (v: string | null): void => {
    onChange(v);
    onDone();
  };
  const toggle = (path: string): void =>
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(path)) n.delete(path);
      else n.add(path);
      return n;
    });
  const activateRow = (r: Row): void => {
    if (r.selectable) choose(r.path);
    else if (r.hasChildren) toggle(r.path);
  };
  const itemsFor = (r: Row): ContextMenuItem[] => [
    ...(r.selectable ? [{ label: "Select", onClick: () => choose(r.path) }] : []),
    ...(r.hasChildren
      ? [{ label: expanded.has(r.path) ? "Collapse" : "Expand", onClick: () => toggle(r.path) }]
      : []),
    {
      label: "Copy name",
      onClick: () => {
        void navigator.clipboard?.writeText(r.path);
      },
    },
  ];

  const onKeyDown = (e: KeyboardEvent): void => {
    const r = rows[active];
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(rows.length - 1, a + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (r) activateRow(r);
    } else if (e.key === "ArrowRight" && mode === "tree" && r?.hasChildren && !expanded.has(r.path)) {
      e.preventDefault();
      toggle(r.path);
    } else if (e.key === "ArrowLeft" && mode === "tree" && r?.hasChildren && expanded.has(r.path)) {
      e.preventDefault();
      toggle(r.path);
    }
  };

  return (
    <div>
      <input
        ref={inputRef}
        aria-label={`${label} search`}
        value={query}
        placeholder="type to filter…"
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
        }}
        onKeyDown={onKeyDown}
        className="mb-1 w-full rounded-sm border border-slate-700 bg-slate-800 px-1.5 py-1 text-[11px] text-slate-200 focus:border-slate-500 focus:outline-hidden"
      />
      <div role="listbox" aria-label={`${label} options`} className="max-h-60 overflow-auto">
        {noneLabel && !filtering && (
          <RowButton selected={value === null} active={false} onClick={() => choose(null)} onHover={() => setActive(-1)}>
            <span className="italic text-slate-500">{noneLabel}</span>
          </RowButton>
        )}
        {rows.length === 0 && <div className="px-2 py-1 text-[11px] text-slate-600">no matches</div>}
        {rows.map((r, i) => (
          <RowButton
            key={r.path}
            selected={value === r.path}
            active={i === active}
            indent={r.depth}
            onClick={() => activateRow(r)}
            onHover={() => setActive(i)}
            onContextMenu={(e) => menu.open(e, itemsFor(r))}
          >
            <span className="w-3 shrink-0 text-slate-600">
              {r.hasChildren ? expanded.has(r.path) ? <ChevronDown size={11} /> : <ChevronRight size={11} /> : null}
            </span>
            <span className={`truncate ${r.selectable ? "" : "text-slate-500"}`}>{r.label}</span>
          </RowButton>
        ))}
      </div>
      {menu.node}
    </div>
  );
}

function RowButton({
  selected,
  active,
  indent = 0,
  onClick,
  onHover,
  onContextMenu,
  children,
}: {
  selected: boolean;
  active: boolean;
  indent?: number;
  onClick: () => void;
  onHover: () => void;
  onContextMenu?: (e: MouseEvent) => void;
  children: ReactNode;
}): ReactElement {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onClick}
      onMouseEnter={onHover}
      onContextMenu={onContextMenu}
      style={{ paddingLeft: `${6 + indent * 14}px` }}
      className={`flex w-full items-center gap-1 rounded-sm py-0.5 pr-2 text-left font-mono text-[11px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 ${
        active ? "bg-slate-700/70" : ""
      } ${selected ? "text-violet-300" : "text-slate-300"}`}
    >
      {children}
    </button>
  );
}
